import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

interface SiteNavbarProps {
  overlay?: boolean;
}

const navLinks = [
  { label: "首页", to: "/" },
  { label: "灵感专题", to: "/inspiration" },
  { label: "搜索", to: "/search" },
  { label: "关于我们", to: "/about" },
  { label: "联系我们", to: "/contact" },
];

export default function SiteNavbar({ overlay = false }: SiteNavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
  }, [location.pathname]);

  const transparent = overlay && !scrolled;
  const textColor = transparent ? "text-background-50" : "text-foreground-800";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        transparent
          ? "bg-gradient-to-b from-foreground-950/40 to-transparent"
          : "bg-background-50/95 backdrop-blur border-b border-background-200/70"
      }`}
    >
      <nav className="w-full px-4 md:px-8 h-16 md:h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
          <span className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center">
            <i className="ri-camera-lens-line text-background-50 text-lg"></i>
          </span>
          <span
            className={`font-heading text-lg md:text-xl font-semibold tracking-wide ${textColor}`}
          >
            女性ins大片
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-7">
          <Link
            to="/"
            className={`text-sm font-medium transition-colors hover:text-primary-500 cursor-pointer ${textColor}`}
          >
            首页
          </Link>
          <div
            className="relative"
            onMouseEnter={() => setCatOpen(true)}
            onMouseLeave={() => setCatOpen(false)}
          >
            <button
              type="button"
              className={`text-sm font-medium transition-colors hover:text-primary-500 cursor-pointer flex items-center gap-1 whitespace-nowrap ${textColor}`}
            >
              分类
              <i className="ri-arrow-down-s-line text-base"></i>
            </button>
            {catOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-52">
                <div className="bg-background-50 rounded-lg border border-background-200/70 py-2 shadow-lg">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.id}`}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      <i className={`${cat.icon} text-base`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          {navLinks.slice(1).map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-sm font-medium transition-colors hover:text-primary-500 cursor-pointer ${textColor}`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/search"
            aria-label="搜索作品"
            className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors hover:bg-primary-500 hover:text-background-50 cursor-pointer ${
              transparent ? "text-background-50 border border-background-50/50" : "text-foreground-700 border border-background-300"
            }`}
          >
            <i className="ri-search-line text-base"></i>
          </Link>
        </div>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMobileOpen((v) => !v)}
          className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${textColor}`}
        >
          <i className={mobileOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"}></i>
        </button>
      </nav>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-3">
          <Link to="/" className="block py-3 text-sm font-medium text-foreground-800 border-b border-background-100 cursor-pointer">
            首页
          </Link>
          <div className="py-3 border-b border-background-100">
            <p className="text-sm font-medium text-foreground-800 mb-2">分类</p>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className="flex items-center gap-2 px-3 py-2 rounded-md bg-primary-50 text-primary-600 text-sm cursor-pointer"
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
          {navLinks.slice(1).map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="block py-3 text-sm font-medium text-foreground-800 border-b border-background-100 last:border-none cursor-pointer"
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}